export type DiagnosticTimings = Record<string, number>;

export type TokenSavings = {
  originalTokens: number | null;
  afterDeterministicTokens: number | null;
  finalTokens: number | null;
  deterministicTokensSaved: number | null;
  modelIncrementalTokensSaved: number | null;
  totalTokensSaved: number | null;
  totalReduction: number | null;
  modelStage: string;
  modelRan: boolean | null;
  fallbackUsed: boolean | null;
  attributionResidualTokens: number | null;
  tokenEstimator: string;
};

export type CompressionResult = {
  compressedText: string;
  originalTokens: number | null;
  compressedTokens: number | null;
  tokenSavings: TokenSavings;
  compressionMode: string;
  compressionPath: string;
  executionType: string;
  elapsedMs: number | null;
  tokenEstimator: string;
  diagnosticTimings: DiagnosticTimings;
  modelGateDecision: string;
  modelGateReason: string;
  warnings: string[];
};

const describeExecutionType = (compressionPath: string) => {
  if (compressionPath === "unchanged") return "Skipped";
  if (compressionPath === "deterministic_only") return "Deterministic only";
  if (compressionPath === "deterministic_plus_model") return "Model ran";
  return compressionPath || "Unknown";
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;

const optionalNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const optionalBoolean = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null;

const diagnosticTimingsFrom = (value: unknown): DiagnosticTimings => {
  const timings = asRecord(value);
  if (!timings) return {};
  return Object.fromEntries(
    Object.entries(timings).filter((entry): entry is [string, number] =>
      typeof entry[1] === "number" && Number.isFinite(entry[1])
    )
  );
};

export const parseCompressionResponse = (payload: unknown, compressionMode: string): CompressionResult => {
  const data = asRecord(payload) ?? {};
  const diagnostics = asRecord(data.diagnostics);
  const savings = asRecord(data.token_savings);
  const originalTokens = optionalNumber(data.original_tokens);
  const compressedTokens = optionalNumber(data.compressed_tokens);
  const afterDeterministicTokens = optionalNumber(
    savings?.after_deterministic_tokens ?? diagnostics?.deterministic_output_tokens
  );
  const deterministicTokensSaved = optionalNumber(
    savings?.deterministic_tokens_saved ?? diagnostics?.deterministic_tokens_saved
  );
  const modelIncrementalTokensSaved = optionalNumber(
    savings?.model_incremental_tokens_saved ?? diagnostics?.model_incremental_tokens_saved
  );
  const totalTokensSaved = optionalNumber(savings?.total_tokens_saved) ?? (
    originalTokens != null && compressedTokens != null ? originalTokens - compressedTokens : null
  );
  const modelRan = optionalBoolean(savings?.model_ran ?? diagnostics?.llmlingua_called);
  const fallbackUsed = optionalBoolean(savings?.fallback_used ?? diagnostics?.fallback_used);
  const compressionPath = String(data.compression_path ?? diagnostics?.compression_path ?? "unknown");
  const tokenEstimator = String(savings?.token_estimator ?? data.token_estimator ?? "");

  return {
    compressedText: String(
      data.compressed_text ??
        data.compressed ??
        data.compressedText ??
        data.output ??
        data.result ??
        data.text ??
        ""
    ),
    originalTokens,
    compressedTokens,
    tokenSavings: {
      originalTokens: optionalNumber(savings?.original_tokens) ?? originalTokens,
      afterDeterministicTokens,
      finalTokens: optionalNumber(savings?.final_tokens) ?? compressedTokens,
      deterministicTokensSaved,
      modelIncrementalTokensSaved,
      totalTokensSaved,
      totalReduction: optionalNumber(savings?.total_reduction) ?? optionalNumber(data.reduction),
      modelStage: String(savings?.model_stage ?? (modelRan ? "llmlingua2" : "")),
      modelRan,
      fallbackUsed,
      attributionResidualTokens: optionalNumber(savings?.attribution_residual_tokens),
      tokenEstimator,
    },
    compressionMode: String(data.compression_mode ?? compressionMode),
    compressionPath,
    executionType: String(
      modelRan === true
        ? "Model ran"
        : data.execution_type ?? data.executionType ?? describeExecutionType(compressionPath)
    ),
    elapsedMs: optionalNumber(data.elapsed_ms),
    tokenEstimator,
    diagnosticTimings: diagnosticTimingsFrom(diagnostics?.timings),
    modelGateDecision: typeof diagnostics?.model_gate_decision === "string"
      ? diagnostics.model_gate_decision
      : "",
    modelGateReason: typeof diagnostics?.model_gate_reason === "string"
      ? diagnostics.model_gate_reason
      : "",
    warnings: Array.isArray(data.warnings) ? data.warnings.map(String) : [],
  };
};

export const buildCompressionRequestPayload = (
  text: string,
  aggressiveness: number,
  compressionMode: string
) => ({
  text,
  aggressiveness: aggressiveness / 100,
  include_sections: false,
  include_diagnostics: true,
  allow_cpu_model_auto: true,
  mode: compressionMode,
});
